/** Docs-URL helpers for EHR provider tiles + detail views. */

import { docsUrl } from "@/lib/docs";

/**
 * Provider `type` → docs slug renames. Only listed here when the internal
 * type differs from the URL slug (e.g. `uhc` → `unitedhealthcare`). Everything
 * else resolves to the type verbatim.
 */
const PATIENT_ACCESS_SLUG_OVERRIDES: Record<string, string> = {
  uhc: "unitedhealthcare",
  eclinicalworks: "ecw",
  medicare: "cms-blue-button",
};

/** Provider types with a dedicated patient-access integration guide. */
const PATIENT_ACCESS_WITH_GUIDES = new Set<string>([
  "epic",
  "cerner",
  "athena",
  "eclinicalworks",
  "uhc",
  "medicare",
  "aetna",
  "meditech",
]);

/** Provider types with a dedicated clinician-workflow integration guide. */
const CLINICIAN_WORKFLOW_WITH_GUIDES = new Set<string>([
  "cerner-clinical-workflow",
]);

/** Provider types with a dedicated backend-services integration guide. */
const BACKEND_WITH_GUIDES = new Set<string>([
  "cerner-backend",
]);

/**
 * Returns the docs URL for a specific EHR provider, or the section index when
 * no provider-specific page exists. Callers use this to link tooltip/help
 * icons to the right integration guide.
 */
export function getProviderDocsUrl(providerType: string): string {
  if (providerType.endsWith("-clinical-workflow")) {
    if (CLINICIAN_WORKFLOW_WITH_GUIDES.has(providerType)) {
      const base = providerType.replace(/-clinical-workflow$/, "");
      return docsUrl(`/docs/clinician-workflow/${base}`);
    }
    return docsUrl("/docs/clinician-workflow");
  }

  if (providerType.endsWith("-backend")) {
    if (BACKEND_WITH_GUIDES.has(providerType)) {
      const base = providerType.replace(/-backend$/, "");
      return docsUrl(`/docs/backend/${base}`);
    }
    return docsUrl("/docs/backend");
  }

  if (PATIENT_ACCESS_WITH_GUIDES.has(providerType)) {
    const slug = PATIENT_ACCESS_SLUG_OVERRIDES[providerType] ?? providerType;
    return docsUrl(`/docs/patient-access/${slug}`);
  }
  return docsUrl("/docs/patient-access");
}
